import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@resource-kit/ui/react';
import type { BoardStage } from '../board/boardModel';
import { STAGE_LABEL } from '../board/BatchBoard';
import { formatBatchDay } from '../board/formatBatchDay';

// Literal keys, so the i18n source-coverage scan can see them.
const TONE_TAG = {
  past: 'dispatch.board.yesterday',
  today: 'dispatch.board.today',
} as const;

interface DispatchDetailPaneProps {
  date: string;
  stage: BoardStage;
  /** The selected cell's count; absent while the board has no row for it. */
  count?: number;
  /** The selected row's total across all columns; absent with `count`. */
  rowTotal?: number;
  /** Yesterday / today / any other date: decides the tag in the title. */
  tone: 'past' | 'today' | 'future';
  /** The board GET succeeded. An empty cell is only believable then. */
  boardReady: boolean;
  children: ReactNode;
}

/**
 * The frame under the batch board for the selected cell (#2439): the title
 * line, the count badge and — for a cell with nothing in it — one empty-state
 * card in place of the column's content. The page decides what the content is;
 * this frame reads nothing but its props, so a pending or failed board GET
 * (`boardReady` false) always renders the content, never the empty card.
 *
 * The exception column never shows the empty state: it is the one place a
 * zero is the good news and the page still carries the way into the list.
 */
export function DispatchDetailPane({
  date,
  stage,
  count,
  rowTotal,
  tone,
  boardReady,
  children,
}: DispatchDetailPaneProps) {
  const { t, i18n } = useTranslation();
  const day = formatBatchDay(date, i18n.language);
  const stageName = t(STAGE_LABEL[stage]);
  const tag = tone === 'future' ? null : t(TONE_TAG[tone]);
  const isEmpty = boardReady && count === 0 && stage !== 'exception_or_cancelled';

  const emptySub = (): string => {
    if (rowTotal === 0) return t('dispatch.pane.empty.noJobs');
    if (stage === 'awaiting_dispatch') return t('dispatch.pane.empty.allDispatched');
    return t('dispatch.pane.empty.notHereYet');
  };

  return (
    <section
      data-testid="dispatch-detail-pane"
      data-stage={stage}
      className="space-y-4 border-t border-border pt-[18px]"
    >
      <h2 className="flex flex-wrap items-center gap-2 text-[19px] font-semibold">
        <span>
          {day}
          {tag ? ` ${tag}` : ''} · {stageName}
        </span>
        {count !== undefined ? (
          <Badge data-testid="dispatch-pane-badge" variant="secondary" className="tabular-nums">
            {t('dispatch.pane.countBadge', { count })}
          </Badge>
        ) : null}
      </h2>
      {isEmpty ? (
        <div
          data-testid="dispatch-pane-empty"
          className="rounded-xl border border-border bg-card px-4 py-5 text-[13px] text-muted-foreground"
        >
          <b className="text-foreground">
            {t('dispatch.pane.empty.title', { day, stage: stageName })}
          </b>
          <br />
          {emptySub()}
        </div>
      ) : (
        children
      )}
    </section>
  );
}
